import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import SubpageHero from "@/components/SubpageHero";
import { RotateCcw, ShieldCheck, Clock, Mail, ArrowRight } from "lucide-react";

const TX = {
  bg: {
    title: "Връщане на средства",
    eyebrow: "Прозрачни условия",
    back: "Начало",
    subtitle:
      "Плащате за полза, не за обещание. Ако платформата не Ви е свършила работа, искаме връщането да бъде кратък разговор, а не процедура.",
    meta: ["14 дни гаранция", "Отмяна по всяко време", "Отговор до 5 работни дни"],
    promise_title: "Нашето обещание",
    promise: [
      { h: "14 дни за размисъл", p: "Първите 14 дни от платен абонамент са без риск. Пишете ни и връщаме сумата без допълнителни въпроси." },
      { h: "Без автоматични изненади", p: "Напомняме Ви по имейл преди всяко подновяване. Отмяната е с един бутон в профила." },
      { h: "Запазвате данните си", p: "След отмяна достъпът до платените функции спира, но дневникът и отчетите Ви остават видими." },
    ],
    sections: [
      {
        h: "Месечни и годишни абонаменти (Pro и Клиника)",
        p: [
          "Можете да отмените по всяко време. Достъпът остава активен до края на вече платения период и след това не се начислява нищо.",
          "В рамките на 14 дни от плащането възстановяваме сумата изцяло, ако сте използвали под една четвърт от включените анализи. Над този дял изчисляваме пропорционално — за реално неизползваната част.",
          "Годишните планове се връщат пропорционално на неизползваните месеци, като се приспада отстъпката, която сте получили за годишно плащане.",
        ],
      },
      {
        h: "Еднократни отключвания и кредити",
        p: [
          "Краткосрочните отключвания дават достъп веднага след плащането. Затова, съгласно европейските правила за цифрово съдържание, те не подлежат на връщане, след като сте ги използвали — при покупката изрично потвърждавате това.",
          "Ако плащането е преминало, но достъпът не се е отворил по техническа причина, възстановяваме сумата изцяло.",
        ],
      },
      {
        h: "Безплатен пробен период",
        p: [
          "Пробният период не изисква карта и не се превръща автоматично в платен абонамент. След изтичането му акаунтът продължава в безплатния план.",
        ],
      },
      {
        h: "Как да заявите връщане",
        p: [
          "Пишете ни от имейла, с който сте се регистрирали, на billing@nutramilo.bg — или използвайте формата за контакт. Достатъчно е да посочите каква сума и за кой период става дума.",
          "Обработваме заявките до 5 работни дни. Сумата се връща по същия начин, по който е платена.",
        ],
      },
      {
        h: "Кога не връщаме",
        p: [
          "При нарушаване на условията за ползване, при опит за злоупотреба с платформата, както и когато над три четвърти от включените анализи вече са използвани.",
        ],
      },
    ],
    cta_title: "Имате въпрос преди да платите?",
    cta_p: "По-добре е да попитате предварително, отколкото да искате връщане после.",
    cta_btn: "Свържете се с нас",
    pricing_btn: "Вижте плановете",
    updated: "Последна актуализация: юни 2026 г.",
    seo_desc: "Условия за връщане на средства в Nutramilo — 14 дни гаранция, абонаменти, отключвания и пробен период.",
  },
  en: {
    title: "Refunds",
    eyebrow: "Transparent terms",
    back: "Home",
    subtitle:
      "You pay for value, not a promise. If the platform hasn't helped you, a refund should be a short conversation — not a procedure.",
    meta: ["14-day guarantee", "Cancel any time", "Reply within 5 business days"],
    promise_title: "Our promise",
    promise: [
      { h: "14 days to decide", p: "The first 14 days of a paid plan are risk-free. Write to us and we return the amount without extra questions." },
      { h: "No silent surprises", p: "We email you before every renewal. Cancelling takes one button in your profile." },
      { h: "You keep your data", p: "After cancelling, paid features stop, but your diary and reports stay visible." },
    ],
    sections: [
      {
        h: "Monthly and annual plans (Pro and Clinic)",
        p: [
          "You can cancel at any time. Access remains active until the end of the period you already paid for, and nothing is charged afterwards.",
          "Within 14 days of payment we refund in full if you have used less than a quarter of the included analyses. Above that we calculate pro-rata, for the part genuinely unused.",
          "Annual plans are refunded pro-rata on unused months, less the discount you received for paying yearly.",
        ],
      },
      {
        h: "One-off unlocks and credits",
        p: [
          "Short-term unlocks grant access immediately after payment. Under EU rules for digital content they are therefore non-refundable once used — you confirm this explicitly at checkout.",
          "If payment went through but access did not open for a technical reason, we refund in full.",
        ],
      },
      {
        h: "Free trial",
        p: [
          "The trial needs no card and never converts into a paid plan automatically. When it ends, the account continues on the free plan.",
        ],
      },
      {
        h: "How to request a refund",
        p: [
          "Write from the email you registered with to billing@nutramilo.bg, or use the contact form. Just tell us the amount and the period.",
          "We process requests within 5 business days. The amount is returned the same way it was paid.",
        ],
      },
      {
        h: "When we don't refund",
        p: [
          "For breaches of the terms of use, attempts to abuse the platform, and when more than three quarters of the included analyses have already been used.",
        ],
      },
    ],
    cta_title: "A question before you pay?",
    cta_p: "Better to ask first than to ask for a refund later.",
    cta_btn: "Contact us",
    pricing_btn: "See the plans",
    updated: "Last updated: June 2026",
    seo_desc: "Nutramilo refund terms — 14-day guarantee, subscriptions, unlocks and the free trial.",
  },
  es: {
    title: "Reembolsos",
    eyebrow: "Condiciones transparentes",
    back: "Inicio",
    subtitle:
      "Paga por un resultado, no por una promesa. Si la plataforma no le ha servido, el reembolso debe ser una conversación breve, no un trámite.",
    meta: ["Garantía de 14 días", "Cancele cuando quiera", "Respuesta en 5 días hábiles"],
    promise_title: "Nuestro compromiso",
    promise: [
      { h: "14 días para decidir", p: "Los primeros 14 días de un plan de pago son sin riesgo. Escríbanos y devolvemos el importe sin más preguntas." },
      { h: "Sin sorpresas", p: "Le avisamos por correo antes de cada renovación. Cancelar es un botón en su perfil." },
      { h: "Sus datos se quedan", p: "Tras cancelar, las funciones de pago se detienen, pero su diario y sus informes siguen visibles." },
    ],
    sections: [
      {
        h: "Planes mensuales y anuales (Pro y Clínica)",
        p: [
          "Puede cancelar en cualquier momento. El acceso continúa hasta el final del periodo ya pagado y después no se cobra nada.",
          "Dentro de los 14 días posteriores al pago reembolsamos el importe completo si ha utilizado menos de una cuarta parte de los análisis incluidos. Por encima de ese umbral calculamos la parte proporcional.",
          "Los planes anuales se reembolsan a prorrata de los meses no utilizados, descontando el descuento recibido por el pago anual.",
        ],
      },
      {
        h: "Desbloqueos puntuales y créditos",
        p: [
          "Los desbloqueos breves dan acceso inmediato tras el pago. Según las normas europeas sobre contenido digital, no son reembolsables una vez utilizados; usted lo confirma expresamente al pagar.",
          "Si el pago se realizó pero el acceso no se abrió por un motivo técnico, reembolsamos el importe completo.",
        ],
      },
      {
        h: "Periodo de prueba gratuito",
        p: [
          "La prueba no requiere tarjeta y nunca se convierte automáticamente en un plan de pago. Al terminar, la cuenta continúa en el plan gratuito.",
        ],
      },
      {
        h: "Cómo solicitar un reembolso",
        p: [
          "Escriba desde el correo con el que se registró a billing@nutramilo.bg o use el formulario de contacto. Indique el importe y el periodo.",
          "Tramitamos las solicitudes en un plazo de 5 días hábiles y devolvemos el importe por el mismo medio de pago.",
        ],
      },
      {
        h: "Cuándo no reembolsamos",
        p: [
          "Por incumplimiento de las condiciones de uso, por intentos de abuso de la plataforma y cuando ya se han consumido más de tres cuartas partes de los análisis incluidos.",
        ],
      },
    ],
    cta_title: "¿Una duda antes de pagar?",
    cta_p: "Mejor preguntar antes que pedir un reembolso después.",
    cta_btn: "Contáctenos",
    pricing_btn: "Ver los planes",
    updated: "Última actualización: junio de 2026",
    seo_desc: "Condiciones de reembolso de Nutramilo — garantía de 14 días, suscripciones y prueba gratuita.",
  },
};

const PROMISE_ICONS = [ShieldCheck, Clock, Mail];

export default function RefundPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language] || TX.bg;

  const cardCls = `p-5 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200/80 shadow-sm"}`;
  const textCls = `text-sm leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`;

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"}`} data-testid="refund-page">
      <SEO title={t.title} description={t.seo_desc} path="/refund" />
      <SubpageNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <div className="pt-8">
          <SubpageBackLink label={t.back} />
        </div>
        <SubpageHero
          eyebrow={t.eyebrow}
          title={t.title}
          subtitle={t.subtitle}
          meta={t.meta}
          icon={RotateCcw}
          accent="#0e5c60"
          testId="refund-hero"
        />

        {/* Обещанието — три кратки карти */}
        <div className="grid sm:grid-cols-3 gap-4 mb-12" data-testid="refund-promise">
          {t.promise.map((p, i) => {
            const Icon = PROMISE_ICONS[i] || ShieldCheck;
            return (
              <div key={p.h} className={cardCls}>
                <Icon className="w-4 h-4 mb-3" style={{ color: "#0e5c60" }} />
                <div className={`text-sm font-semibold mb-1.5 ${isDark ? "text-white" : "text-[#101828]"}`}>{p.h}</div>
                <p className={`text-xs leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>{p.p}</p>
              </div>
            );
          })}
        </div>

        {/* Подробни условия */}
        <div className="space-y-10" data-testid="refund-sections">
          {t.sections.map((s, i) => (
            <section key={s.h} data-testid={`refund-section-${i}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <span className={`text-xs font-mono ${isDark ? "text-slate-600" : "text-slate-300"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className={`text-lg font-semibold tracking-tight ${isDark ? "text-white" : "text-[#101828]"}`}>
                  {s.h}
                </h2>
              </div>
              <div className="pl-7 space-y-3">
                {s.p.map((p, j) => <p key={j} className={textCls}>{p}</p>)}
              </div>
            </section>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`mt-14 p-6 sm:p-8 rounded-3xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200/80 shadow-sm"}`}
          data-testid="refund-cta"
        >
          <h2 className={`text-xl font-semibold mb-2 ${isDark ? "text-white" : "text-[#101828]"}`}>{t.cta_title}</h2>
          <p className={`${textCls} mb-5`}>{t.cta_p}</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="refund-contact-cta"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full text-sm font-semibold text-white"
              style={{ background: "linear-gradient(135deg,#0e5c60,#287a2e)" }}
            >
              {t.cta_btn} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              data-testid="refund-pricing-cta"
              className={`inline-flex items-center gap-2 px-5 h-11 rounded-full text-sm font-semibold border ${
                isDark ? "border-white/15 text-slate-200 hover:bg-white/5" : "border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {t.pricing_btn}
            </Link>
          </div>
        </div>

        <p className={`text-xs mt-8 ${isDark ? "text-slate-600" : "text-slate-400"}`}>{t.updated}</p>
      </div>
      <SubpageFooter />
    </div>
  );
}
